import { useState } from "react";
import { Link } from "react-router-dom";
import PageLayout from "@/components/PageLayout";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Package,
  MessageCircle,
  IndianRupee,
  Truck,
  CheckCircle2,
  ChevronRight,
  ShieldCheck,
} from "lucide-react";

const ADMIN_WA_NUMBER = "917624853840";
const SAMPLE_PRICE = 299;

const QUALITIES = [
  { id: "600", label: "600 SD (Cambric)" },
  { id: "600-gold", label: "600 Gold — Premium Single Dyed", badge: "NEW" },
  { id: "060", label: "060 DD (Double Dyed Cambric)" },
  { id: "006", label: "006 DD Mercerised" },
  { id: "6000", label: "6000 SD (Big Panna Shrinkless)" },
  { id: "super-queen", label: "Super Queen" },
  { id: "303", label: "303 Pure Cotton 2x1" },
  { id: "3000", label: "3000 Pure Cotton (Big Panna)" },
  { id: "american-crepe", label: "American Crepe" },
];

const STEPS = [
  {
    n: 1,
    title: "Choose qualities",
    desc: "Select all 9 or pick the specific ones you want in your sample kit — includes our new 600 Gold.",
  },
  {
    n: 2,
    title: "Fill in your details",
    desc: "Name, phone and full delivery address — anywhere in India.",
  },
  {
    n: 3,
    title: "Pay ₹299 via UPI",
    desc: "Our team will share the UPI ID / QR over WhatsApp once your details are received.",
  },
  {
    n: 4,
    title: "Receive in 2–3 days",
    desc: "Sealed kit with labelled fabric swatches arrives at your address by courier.",
  },
];

const buildWaLink = ({ name, phone, email, address, city, pincode, qualities, notes }) => {
  const selectedLabels = qualities
    .map((id) => QUALITIES.find((q) => q.id === id)?.label)
    .filter(Boolean);

  const lines = [
    "*Sample Kit Request — Mutha Paras & Co.*",
    "",
    `*Customer:* ${name}`,
    `*Phone:* ${phone}`,
    email ? `*Email:* ${email}` : null,
    "",
    "*Delivery Address:*",
    address,
    `${city}${pincode ? " — " + pincode : ""}`,
    "",
    `*Qualities Requested* (${selectedLabels.length}):`,
    ...selectedLabels.map((q, i) => `${i + 1}. ${q}`),
    "",
    notes ? `*Special Requests:* ${notes}` : null,
    notes ? "" : null,
    `*Payment:* ₹${SAMPLE_PRICE} via UPI — please share the UPI ID / QR.`,
    "",
    "_Sent via muthaparas.com — Sample Kit form_",
  ].filter(Boolean);
  return `https://wa.me/${ADMIN_WA_NUMBER}?text=${encodeURIComponent(lines.join("\n"))}`;
};

export default function SampleKitPage() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    city: "",
    pincode: "",
    notes: "",
  });
  const [selected, setSelected] = useState(new Set(QUALITIES.map((q) => q.id)));
  const [submitted, setSubmitted] = useState(false);
  const [waLink, setWaLink] = useState("");
  const [error, setError] = useState("");

  const update = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const toggleQuality = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(QUALITIES.map((q) => q.id)));
  const clearAll = () => setSelected(new Set());

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");

    if (!form.name.trim()) return setError("Please enter your name.");
    if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 10)
      return setError("Please enter a valid phone number (at least 10 digits).");
    if (!form.address.trim()) return setError("Please enter your delivery address.");
    if (!form.city.trim()) return setError("Please enter your city.");
    if (selected.size === 0)
      return setError("Please pick at least one quality for your sample kit.");

    const link = buildWaLink({ ...form, qualities: Array.from(selected) });
    setWaLink(link);
    setSubmitted(true);
    window.open(link, "_blank", "noopener");
  };

  return (
    <PageLayout>
      <SEO
        title="₹299 Sample Kit — Feel All 9 Lining Fabric Qualities | Mutha Paras & Co."
        description="Order a sealed sample kit with real fabric swatches of all 9 lining qualities including the new 600 Gold. Pay ₹299 via UPI, dispatched pan-India in 2–3 days."
        path="/sample-kit"
      />
      <main
        className="overflow-hidden"
        style={{ backgroundColor: "var(--mp-paper)" }}
        data-testid="sample-kit-page"
      >
        {/* ============ Hero ============ */}
        <section className="py-16 lg:py-20 px-6 lg:px-10" data-testid="sample-hero">
          <div className="max-w-5xl mx-auto text-center">
            <div
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-xs font-medium uppercase tracking-[0.2em] mb-6"
              style={{
                backgroundColor: "var(--mp-surface-2)",
                color: "var(--mp-copper)",
                fontFamily: "'Manrope', sans-serif",
              }}
            >
              <Package className="w-3.5 h-3.5" />
              Sample Kit · Pan-India Delivery
            </div>
            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
            >
              Feel the Fabric Before You Order
            </h1>
            <p
              className="mt-6 text-base md:text-lg max-w-3xl mx-auto leading-relaxed"
              style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
            >
              Order a sealed sample kit containing physical swatches of all 9 of our fabric qualities — including our newest 600 Gold — for a
              flat ₹{SAMPLE_PRICE}. Feel the weave, see the colours, and confirm what works for your line
              before placing a bulk order.
            </p>
            <div className="mt-8 flex items-center justify-center gap-2">
              <span
                className="inline-flex items-center gap-1.5 text-2xl md:text-3xl font-medium"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text-strong)" }}
                data-testid="sample-price"
              >
                <IndianRupee className="w-5 h-5" />
                {SAMPLE_PRICE}
              </span>
              <span
                className="text-xs uppercase tracking-[0.2em] ml-2"
                style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
              >
                Flat · Courier Included
              </span>
            </div>
          </div>
        </section>

        {/* ============ How it works ============ */}
        <section className="py-12 lg:py-16 px-6 lg:px-10" data-testid="sample-steps">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
              {STEPS.map((s, i) => (
                <div
                  key={s.n}
                  className="bg-white border border-[var(--mp-line)] rounded-2xl p-6 lg:p-7 animate-fade-in-up"
                  style={{ animationDelay: `${i * 80}ms` }}
                  data-testid={`sample-step-${s.n}`}
                >
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold mb-5"
                    style={{
                      backgroundColor: "var(--mp-solid)",
                      color: "#FFFFFF",
                      fontFamily: "'Manrope', sans-serif",
                    }}
                  >
                    {s.n}
                  </div>
                  <h3
                    className="text-lg font-medium mb-2"
                    style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
                  >
                    {s.title}
                  </h3>
                  <p
                    className="text-sm leading-relaxed"
                    style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
                  >
                    {s.desc}
                  </p>
                </div>
              ))}
            </div>

            {/* Reassurance bar */}
            <div
              className="mt-8 flex flex-col sm:flex-row gap-4 sm:gap-8 items-center justify-center bg-[var(--mp-paper)] border border-[var(--mp-line)] rounded-2xl py-5 px-6"
              data-testid="sample-reassurance"
            >
              <div className="flex items-center gap-2 text-sm" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}>
                <Truck className="w-4 h-4" style={{ color: "var(--mp-text-strong)" }} />
                <span>Delivery in 2–3 working days across India</span>
              </div>
              <div className="hidden sm:block w-px h-5 bg-[var(--mp-line)]" />
              <div className="flex items-center gap-2 text-sm" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}>
                <ShieldCheck className="w-4 h-4" style={{ color: "var(--mp-text-strong)" }} />
                <span>Sealed kit with labelled swatches of every quality</span>
              </div>
            </div>
          </div>
        </section>

        {/* ============ Form ============ */}
        <section className="py-16 lg:py-20 px-6 lg:px-10" data-testid="sample-form-section">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h2
                className="text-3xl md:text-4xl font-medium tracking-tight"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
              >
                Place Your Sample Kit Order
              </h2>
              <p
                className="mt-3 text-sm md:text-base"
                style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
              >
                It takes about 60 seconds. Your details and chosen qualities go straight to our WhatsApp.
              </p>
            </div>

            {submitted ? (
              <div
                className="bg-white border border-[var(--mp-line)] rounded-2xl p-10 text-center animate-fade-in pastel-green"
                data-testid="sample-submitted"
              >
                <CheckCircle2 className="w-14 h-14 mx-auto mb-4" style={{ color: "var(--mp-text-strong)" }} />
                <h3
                  className="text-2xl md:text-3xl font-medium mb-3"
                  style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
                >
                  Order Details Captured!
                </h3>
                <p className="text-sm mb-2" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}>
                  A WhatsApp window has opened with your sample kit request.
                </p>
                <p className="text-sm mb-6" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text)" }}>
                  Please tap <strong>Send</strong> there — our team will reply with the UPI ID / QR for the
                  ₹{SAMPLE_PRICE} payment, then dispatch your kit within 2–3 working days.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  {waLink && (
                    <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="sample-resend-wa">
                      <Button
                        className="rounded-sm text-sm px-6 py-5 text-white whatsapp-pulse"
                        style={{ backgroundColor: "#25D366", fontFamily: "'Manrope', sans-serif" }}
                      >
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Re-open WhatsApp
                      </Button>
                    </a>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => { setSubmitted(false); setWaLink(""); }}
                    className="rounded-sm text-sm px-6 py-5"
                    style={{ borderColor: "var(--mp-solid)", color: "var(--mp-text-strong)", fontFamily: "'Manrope', sans-serif" }}
                    data-testid="sample-new-order"
                  >
                    Place Another Sample Order
                  </Button>
                </div>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="bg-white border border-[var(--mp-line)] rounded-2xl p-6 sm:p-8 lg:p-10 space-y-7"
                data-testid="sample-form"
              >
                {/* Personal */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider font-medium mb-2 block" style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}>
                      Full Name *
                    </Label>
                    <Input
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      placeholder="Your name / company"
                      autoComplete="name"
                      className="rounded-sm border-[var(--mp-line)] py-5 text-sm"
                      data-testid="sample-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider font-medium mb-2 block" style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}>
                      Phone (WhatsApp) *
                    </Label>
                    <Input
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      placeholder="+91 XXXXXXXXXX"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      className="rounded-sm border-[var(--mp-line)] py-5 text-sm"
                      data-testid="sample-phone-input"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-[10px] uppercase tracking-wider font-medium mb-2 block" style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}>
                    Email <span className="opacity-60">(optional)</span>
                  </Label>
                  <Input
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="you@example.com"
                    type="email"
                    autoComplete="email"
                    className="rounded-sm border-[var(--mp-line)] py-5 text-sm"
                    data-testid="sample-email-input"
                  />
                </div>

                {/* Address */}
                <div>
                  <Label className="text-[10px] uppercase tracking-wider font-medium mb-2 block" style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}>
                    Delivery Address *
                  </Label>
                  <Textarea
                    value={form.address}
                    onChange={(e) => update("address", e.target.value)}
                    placeholder="House / shop, street, area, landmark…"
                    rows={3}
                    className="rounded-sm border-[var(--mp-line)] text-sm"
                    data-testid="sample-address-input"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider font-medium mb-2 block" style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}>
                      City / State *
                    </Label>
                    <Input
                      value={form.city}
                      onChange={(e) => update("city", e.target.value)}
                      placeholder="e.g. Bangalore, Karnataka"
                      className="rounded-sm border-[var(--mp-line)] py-5 text-sm"
                      data-testid="sample-city-input"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider font-medium mb-2 block" style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}>
                      Pincode
                    </Label>
                    <Input
                      value={form.pincode}
                      onChange={(e) => update("pincode", e.target.value)}
                      placeholder="560001"
                      inputMode="numeric"
                      className="rounded-sm border-[var(--mp-line)] py-5 text-sm"
                      data-testid="sample-pincode-input"
                    />
                  </div>
                </div>

                {/* Qualities */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-[10px] uppercase tracking-wider font-medium block" style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}>
                      Qualities you&apos;d like in your kit ({selected.size}/{QUALITIES.length})
                    </Label>
                    <div className="flex gap-3 text-[11px]" style={{ fontFamily: "'Manrope', sans-serif" }}>
                      <button
                        type="button"
                        onClick={selectAll}
                        className="hover:underline"
                        style={{ color: "var(--mp-text-strong)" }}
                        data-testid="sample-select-all"
                      >
                        Select all
                      </button>
                      <button
                        type="button"
                        onClick={clearAll}
                        className="hover:underline"
                        style={{ color: "var(--mp-copper)" }}
                        data-testid="sample-clear-all"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                  <div
                    className="grid grid-cols-1 sm:grid-cols-2 gap-2"
                    data-testid="sample-qualities-grid"
                  >
                    {QUALITIES.map((q) => {
                      const checked = selected.has(q.id);
                      return (
                        <label
                          key={q.id}
                          className={`flex items-center gap-3 px-4 py-3 border rounded-sm cursor-pointer transition ${
                            checked ? "bg-[var(--mp-paper)]" : "bg-white hover:bg-[var(--mp-paper)]"
                          }`}
                          style={{ borderColor: checked ? "#101010" : "#E4DDCE" }}
                          data-testid={`sample-quality-${q.id}`}
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => toggleQuality(q.id)}
                            data-testid={`sample-quality-checkbox-${q.id}`}
                            aria-label={q.label}
                          />
                          <span
                            className="text-sm"
                            style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text)" }}
                          >
                            {q.label}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <Label className="text-[10px] uppercase tracking-wider font-medium mb-2 block" style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}>
                    Special Requests <span className="opacity-60">(optional)</span>
                  </Label>
                  <Textarea
                    value={form.notes}
                    onChange={(e) => update("notes", e.target.value)}
                    placeholder="Preferred colour family, specific shade interest, urgent delivery, etc."
                    rows={2}
                    className="rounded-sm border-[var(--mp-line)] text-sm"
                    data-testid="sample-notes-input"
                  />
                </div>

                {/* Error */}
                {error && (
                  <div
                    className="text-xs px-4 py-3 rounded-sm border"
                    style={{
                      borderColor: "#fecaca",
                      backgroundColor: "#fef2f2",
                      color: "#b91c1c",
                      fontFamily: "'Manrope', sans-serif",
                    }}
                    data-testid="sample-error"
                  >
                    {error}
                  </div>
                )}

                {/* Submit */}
                <div className="pt-2 border-t border-[var(--mp-line)] flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="text-sm" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}>
                    Total payable: <span className="font-semibold" style={{ color: "var(--mp-text)" }}>₹{SAMPLE_PRICE}</span>{" "}
                    (UPI details shared on WhatsApp)
                  </div>
                  <Button
                    type="submit"
                    className="rounded-sm text-sm px-6 py-5 text-white whatsapp-pulse"
                    style={{ backgroundColor: "#25D366", fontFamily: "'Manrope', sans-serif" }}
                    data-testid="sample-submit-btn"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Send Request via WhatsApp
                  </Button>
                </div>
              </form>
            )}
          </div>
        </section>

        {/* ============ FAQ ============ */}
        <section className="py-16 lg:py-20 px-6 lg:px-10 bg-white" data-testid="sample-faq">
          <div className="max-w-4xl mx-auto">
            <h2
              className="text-3xl md:text-4xl font-medium tracking-tight text-center mb-10"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
            >
              Frequently Asked
            </h2>
            <div className="space-y-4">
              {[
                {
                  q: "Is the ₹299 refundable?",
                  a: "It is credited towards your first bulk order with us above ₹5,000 — so think of it as a small commitment fee that gets adjusted on your real order.",
                },
                {
                  q: "Do you ship internationally?",
                  a: "Sample kits are currently India-only. For international clients, we have a dedicated process — please WhatsApp us at +91 76248 53840.",
                },
                {
                  q: "Can I request a specific colour family in my swatches?",
                  a: "Yes — use the Special Requests field. We will try to include shades matching your preference (pastels, jewel tones, neutrals, etc.).",
                },
                {
                  q: "How is the payment made?",
                  a: "Once you submit the form, our team will share a UPI ID / QR code on WhatsApp. Pay ₹299, share the screenshot, and we dispatch the kit by courier within 2–3 working days.",
                },
              ].map((f, i) => (
                <details
                  key={f.q}
                  className="bg-[var(--mp-paper)] border border-[var(--mp-line)] rounded-2xl p-5 group"
                  data-testid={`sample-faq-${i}`}
                >
                  <summary
                    className="cursor-pointer flex items-center justify-between gap-4 list-none"
                    style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text)" }}
                  >
                    <span className="text-base font-medium">{f.q}</span>
                    <ChevronRight className="w-4 h-4 transition-transform group-open:rotate-90 flex-shrink-0" style={{ color: "var(--mp-text-strong)" }} />
                  </summary>
                  <p
                    className="mt-3 text-sm leading-relaxed"
                    style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
                  >
                    {f.a}
                  </p>
                </details>
              ))}
            </div>

            <div className="mt-12 text-center">
              <p
                className="text-sm"
                style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
              >
                Looking to place a bulk order directly instead?
              </p>
              <Link
                to="/order"
                className="inline-flex items-center gap-1 mt-2 text-sm font-medium hover:gap-2 transition-all"
                style={{ color: "var(--mp-text-strong)", fontFamily: "'Manrope', sans-serif" }}
                data-testid="sample-go-to-order"
              >
                Go to the Order Hub <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>
    </PageLayout>
  );
}
